import { useHandler } from "@redotech/react-util/hook";
import { StyleShadowRoot } from "@redotech/react-util/style";
import {
  HsaAutocheck,
  HsaPdpWidgetPlacement,
} from "@redotech/redo-conversion-core-common/conversion/hsa-autocheck";
import { ModalRootContext } from "@redotech/redo-web/modal-root";
import { memo } from "react";
import { StorefrontClient } from "../../client/storefront";
import {
  isHsaIntentSignalEnabled,
  recordHsaIntentSignal,
} from "../../hsa/intent-signals";
import STYLE_MANAGER from "../../style-manager";
import { useCart } from "../../utils/cart";
import { isRecurringCartItem } from "./hsa-cart-summary";
import { HsaPdpBadge } from "./hsa-pdp-badge";
import { useHsaPdpPortals } from "./use-hsa-pdp-portals";
import { useHsaVariantEligibility } from "./use-hsa-variant-eligibility";

export const HsaPdpInitializer = memo(function HsaPdpInitializer({
  hsa,
  placement,
  storefrontClient,
}: {
  hsa: HsaAutocheck;
  placement: HsaPdpWidgetPlacement;
  storefrontClient: StorefrontClient;
}) {
  const variant = useHsaVariantEligibility({ storefrontClient });
  const cart = useCart();
  const portals = useHsaPdpPortals({ placement });
  // Opening the walkthrough is the shopper's HSA/FSA interest signal: float the
  // method at checkout for this cart, never hide the others.
  const onLearnMore = useHandler(() => {
    if (!isHsaIntentSignalEnabled(hsa, "pdp_widget")) {
      return;
    }
    void recordHsaIntentSignal(storefrontClient, {
      source: "pdp_widget",
      exclusive: false,
    });
  });

  if (portals.length === 0 || variant === undefined) {
    return null;
  }

  const subscriptionSignals = {
    variantOffersSubscription: variant.offersSubscription,
    cartHasSubscription: cart?.items.some(isRecurringCartItem) ?? false,
  };

  return (
    <>
      {portals.map(({ portal }, index) => (
        <StyleShadowRoot
          key={index}
          shadowRoot={portal.root}
          styleManager={STYLE_MANAGER}
        >
          <ModalRootContext.Provider value={portal.modalContainer}>
            <HsaPdpBadge
              eligibility={variant.eligibility}
              onLearnMore={onLearnMore}
              subscriptionSignals={subscriptionSignals}
            />
          </ModalRootContext.Provider>
        </StyleShadowRoot>
      ))}
    </>
  );
});
